/**
 * Library scan coordinator — singleton that owns an in-flight library scan
 * independently of any React component's lifetime.
 *
 * v2 — Streaming batches
 * ----------------------
 * The Connector now emits `scan-batch` Tauri events as it scans, each
 * containing ~500 tracks. The coordinator ingests each batch to the backend
 * as it arrives (with skip_rebuild=true), so albums start appearing in the
 * grid within the first minute — not after 25 minutes. When the scan
 * finishes, one `rebuildAlbums()` call materializes the full album table
 * and the library store cache is invalidated so the grid refreshes.
 */

import {
  scanLibrary,
  scanLocalLibrarySources,
  cancelScan as nativeCancelScan,
  onScanProgress,
  onScanBatch,
  onLocalScanProgress,
  getMachineId,
  setLibraryPaths,
  enableHomeLibraryRemoteAccess,
  setAuthToken,
} from '../lib/connector';
import type { ScannedTrack, ScanSummary } from '../lib/connector';
import { libraryService, probeLocalLibraryIpc } from './libraryService';
import { isRemoteLibraryViewer, probeLocalLibraryHost } from './localLibraryIpcGate';
import { useOperationProgressStore } from '../store/operationProgressStore';
import { useLibraryCacheStore } from '../store/libraryCacheStore';
import { useLibraryStore } from '../store/libraryStore';
import { ALBUM_INDEX_FIRST_CHUNK, getLocalAlbumIndexSnapshot } from './libraryAlbumIndex';
import { shouldUseLocalLibraryIpc } from './localLibraryIpcGate';
import {
  collapseBulkScanPlan,
  dedupeTargetedScanSources,
  findEnclosingSource,
  isTargetedScanPlan,
  MULTI_SELECT_SCAN_THRESHOLD,
  normScanPath,
} from './scanPlanUtils';
import { saveScanBaseline, formatDiscoveredFilesCount, formatTagReadProgress, finalizeStageDisplayLabel } from './scanTimingEstimate';
import {
  computeHonestScanProgress,
  enrichHonestScanMetrics,
  formatScanCompleteSummary,
  formatScanningStatusLine,
  hasScanProgressContract,
  isScanProgressTerminalEvent,
  parseHonestScanMetrics,
  resetHonestScanProgressMonotonic,
  scanModeFromProgress,
  updateAlbumsIdentifiedPeak,
  walkTracksFoundFromProgress,
} from './scanProgressTruth';
import { useScanSetAsideUIStore } from '../store/scanSetAsideUIStore';
import { isPermissionDeniedMessage } from '../utils/sourcePermissionErrors';
import {
  beginArtworkProgress,
  getArtworkProgress,
  resetArtworkProgress,
  subscribeArtworkProgress,
  markRemainingArtworkPendingExhausted,
} from '../hooks/useLibraryImageEnrichment';
import {
  buildWatchdogRescueLabel,
  logScanCompletedViaConnector,
  logScanWatchdogRescue,
  markScanProgressWatchdogComplete,
  noteScanProgressWatchdog,
  resetScanProgressWatchdog,
  startScanProgressWatchdog,
  stopScanProgressWatchdog,
} from './scanProgressWatchdog';

const ARTWORK_IDLE_MS = 30_000;
const ENRICHMENT_PROBE_MS = 15_000;

let artworkWatchUnsub: (() => void) | null = null;
let artworkIdleTimer: ReturnType<typeof setTimeout> | null = null;
let coverStatsPollTimer: ReturnType<typeof setInterval> | null = null;
let lastArtworkSnap = { total: 0, withArtwork: 0, exhausted: 0 };
let lastCoverStatsWithCover = 0;
/** Debounce grid merge-refresh storms during cover enrichment. */
let lastEnrichmentCatalogEventAt = 0;
const ENRICHMENT_CATALOG_EVENT_MIN_MS = 12_000;
/** Prevents late poll/subscription ticks from reopening a finished cover-fill op. */
let artworkPhaseFinished = false;
/** True after local walk/tag/extract drain — UI must not wait for network cover enrichment. */
let localScanUiCompleted = false;
let backgroundCoverOperationId: string | null = null;
let enrichmentBannerDismissed = false;
let enrichmentProbeTimer: ReturnType<typeof setInterval> | null = null;
const enrichmentBannerListeners = new Set<() => void>();

function notifyEnrichmentBannerListeners(): void {
  for (const fn of enrichmentBannerListeners) {
    try { fn(); } catch { /* ignore */ }
  }
}

export function subscribeEnrichmentBanner(listener: () => void): () => void {
  enrichmentBannerListeners.add(listener);
  return () => { enrichmentBannerListeners.delete(listener); };
}

export function isEnrichmentBannerDismissed(): boolean {
  return enrichmentBannerDismissed;
}

export function dismissEnrichmentBanner(): void {
  if (enrichmentBannerDismissed) return;
  enrichmentBannerDismissed = true;
  notifyEnrichmentBannerListeners();
}

export function showEnrichmentBanner(): void {
  if (!enrichmentBannerDismissed) return;
  enrichmentBannerDismissed = false;
  notifyEnrichmentBannerListeners();
}

export function isEnrichmentProgressActive(): boolean {
  return backgroundCoverOperationId != null && !artworkPhaseFinished;
}

/** True once post-scan cover enrichment has gone idle — grid should stop polling merges. */
export function isArtworkEnrichmentFinished(): boolean {
  return artworkPhaseFinished;
}

/** Module-level monotonic peaks for getScan*Count helpers (survives nativeProgress field swaps). */
let scanDisplayAlbumsPeak = 0;
let scanDisplayFilesPeak = 0;

function resetScanDisplayPeaks(albumFloor = 0): void {
  scanDisplayAlbumsPeak = Math.max(0, albumFloor);
  scanDisplayFilesPeak = 0;
}

function bumpScanDisplayPeaks(p: Record<string, unknown>, scan: ScanState): void {
  const albumsRaw = typeof p.albums_identified === 'number' && p.albums_identified >= 0
    ? p.albums_identified
    : 0;
  scanDisplayAlbumsPeak = Math.max(scanDisplayAlbumsPeak, albumsRaw, scan.albumCount ?? 0);
  if (scanOperationId) {
    scanDisplayAlbumsPeak = updateAlbumsIdentifiedPeak(
      scanOperationId,
      p,
      scanDisplayAlbumsPeak,
    );
  }

  const fileCandidates = [
    typeof p.audio_files === 'number' ? p.audio_files : 0,
    typeof p.entries_seen === 'number' ? p.entries_seen : 0,
    typeof p.current_file === 'number' ? p.current_file : 0,
    typeof p.files_found === 'number' ? p.files_found : 0,
    scan.ingestDone ?? 0,
  ];
  scanDisplayFilesPeak = Math.max(scanDisplayFilesPeak, ...fileCandidates);
}

function getSessionAlbumGridCount(): number {
  try {
    if (shouldUseLocalLibraryIpc()) {
      return getLocalAlbumIndexSnapshot().albums?.length ?? 0;
    }
    return useLibraryCacheStore.getState().albums?.length ?? 0;
  } catch {
    return 0;
  }
}

function buildInitialScanLabel(opts: {
  selectedPaths: string[];
  scanSources?: ScanSource[];
  prebuiltSources?: ScanSource[];
}): string {
  const folderCount = (opts.selectedPaths?.length ?? 0)
    || (opts.scanSources?.length ?? 0)
    || (opts.prebuiltSources?.length ?? 0);
  let albumsSoFar = 0;
  try {
    albumsSoFar = getSessionAlbumGridCount();
  } catch { /* cache store optional */ }

  const parts: string[] = [];
  if (folderCount > 0) {
    parts.push(`${folderCount} folder${folderCount !== 1 ? 's' : ''}`);
  }
  if (albumsSoFar > 0) {
    parts.push(`${albumsSoFar.toLocaleString()} albums so far`);
  }
  if (parts.length > 0) {
    return `Scanning — ${parts.join(' · ')}…`;
  }
  return 'Scanning — starting…';
}

function stopArtworkProgressWatch(): void {
  if (artworkWatchUnsub) {
    artworkWatchUnsub();
    artworkWatchUnsub = null;
  }
  if (artworkIdleTimer) {
    clearTimeout(artworkIdleTimer);
    artworkIdleTimer = null;
  }
  if (coverStatsPollTimer) {
    clearInterval(coverStatsPollTimer);
    coverStatsPollTimer = null;
  }
  resetArtworkProgress();
  lastArtworkSnap = { total: 0, withArtwork: 0 };
  if (backgroundCoverOperationId) {
    useOperationProgressStore.getState().endOperation(backgroundCoverOperationId);
    backgroundCoverOperationId = null;
  }
}

function resetLocalScanUiCompleted(): void {
  localScanUiCompleted = false;
}

function shouldCompleteLocalScanPhase(p: Record<string, unknown>): boolean {
  if (localScanUiCompleted) return false;
  const phase = String(p.phase ?? '');
  return phase === 'scan_complete' && p.terminal === true;
}

function finishArtworkPhase(
  operationId: string,
  snap?: { total: number; withArtwork: number; exhausted?: number },
): void {
  if (artworkPhaseFinished) return;
  artworkPhaseFinished = true;

  if (artworkWatchUnsub) {
    artworkWatchUnsub();
    artworkWatchUnsub = null;
  }
  if (artworkIdleTimer) {
    clearTimeout(artworkIdleTimer);
    artworkIdleTimer = null;
  }
  if (coverStatsPollTimer) {
    clearInterval(coverStatsPollTimer);
    coverStatsPollTimer = null;
  }

  const total = snap?.total ?? lastArtworkSnap.total;
  const withArtwork = snap?.withArtwork ?? lastArtworkSnap.withArtwork;
  const exhausted = snap?.exhausted ?? lastArtworkSnap.exhausted ?? 0;
  lastArtworkSnap = { total, withArtwork, exhausted };

  resetArtworkProgress();

  let label: string;
  if (total > 0) {
    if (exhausted > 0) {
      label = `${withArtwork.toLocaleString()} covers filled · ${exhausted.toLocaleString()} have no artwork`;
    } else {
      label = `Covers filled in — ${withArtwork.toLocaleString()} of ${total.toLocaleString()}`;
    }
  } else {
    label = 'Cover fill complete';
  }

  useOperationProgressStore.getState().updateOperation(operationId, {
    label,
    phase: 'done',
    progress: 1,
    currentStep: withArtwork,
    totalSteps: total || undefined,
    coverEnrichment: true,
    artworkFilled: withArtwork,
    artworkExhausted: exhausted,
  });
  if (backgroundCoverOperationId === operationId) {
    backgroundCoverOperationId = null;
  }
  stopEnrichmentProgressProbe();
  window.setTimeout(() => {
    useOperationProgressStore.getState().endOperation(operationId);
  }, 2500);
}

function scheduleArtworkIdleCheck(operationId: string): void {
  if (artworkPhaseFinished) return;
  if (artworkIdleTimer) clearTimeout(artworkIdleTimer);
  artworkIdleTimer = setTimeout(() => {
    if (artworkPhaseFinished) return;
    markRemainingArtworkPendingExhausted();
    const snap = getArtworkProgress();
    finishArtworkPhase(operationId, snap);
  }, ARTWORK_IDLE_MS);
}

type ArtworkProgressSnap = {
  total: number;
  withArtwork: number;
  exhausted?: number;
  pending?: number;
  missing?: number;
};

function onArtworkProgressTick(
  operationId: string,
  snap: ArtworkProgressSnap,
): void {
  if (artworkPhaseFinished) return;

  const total = snap.total;
  const withArtwork = snap.withArtwork;
  const exhausted = snap.exhausted ?? 0;
  const resolved = withArtwork + exhausted;
  const progress = total > 0 ? Math.min(0.99, resolved / total) : 0;

  const doneCount = Math.min(total, resolved);
  useOperationProgressStore.getState().updateOperation(operationId, {
    label: total > 0
      ? `Finding covers & album details — ${doneCount.toLocaleString()} of ${total.toLocaleString()}`
      : 'Finding covers & album details…',
    phase: 'artwork',
    progress,
    currentStep: doneCount,
    totalSteps: total,
    coverEnrichment: true,
    artworkFilled: withArtwork,
    artworkExhausted: exhausted,
  });

  if (total > 0 && resolved >= total) {
    finishArtworkPhase(operationId, { total, withArtwork, exhausted });
    return;
  }

  if (
    total !== lastArtworkSnap.total
    || withArtwork !== lastArtworkSnap.withArtwork
    || exhausted !== lastArtworkSnap.exhausted
  ) {
    lastArtworkSnap = { total, withArtwork, exhausted };
    scheduleArtworkIdleCheck(operationId);
  }
}

function startBackgroundCoverEnrichment(albumCountHint: number): void {
  if (backgroundCoverOperationId) return;
  enrichmentBannerDismissed = false;
  notifyEnrichmentBannerListeners();
  const artOpId = useOperationProgressStore.getState().beginOperation('Finding covers & album details…', {
    phase: 'artwork',
    progress: 0,
    coverEnrichment: true,
  });
  backgroundCoverOperationId = artOpId;
  startArtworkProgressWatch(artOpId, albumCountHint);
}

/** Start cover/enrichment progress UI when post-scan work is running without a scan op id. */
export function ensureEnrichmentProgressWatch(albumCountHint = 0): void {
  if (backgroundCoverOperationId || artworkPhaseFinished) return;
  if (isLibraryScanInProgress()) return;
  if (typeof window === 'undefined' || !('__TAURI__' in window)) return;

  void (async () => {
    try {
      const { shouldUseLocalLibraryIpc } = await import('./localLibraryIpcGate');
      if (!shouldUseLocalLibraryIpc()) return;
      const resp = await libraryService.getCoverStats();
      const total = Number(resp?.total ?? 0);
      if (total <= 0) return;
      const artCorpusTotal = Number(resp?.artCorpusTotal ?? resp?.art_corpus_total ?? 0);
      const artTerminal = Number(resp?.artTerminal ?? resp?.art_terminal ?? 0);
      const withoutCover = Number(resp?.withoutCover ?? resp?.none ?? 0);
      const withCover = Number(resp?.withCover ?? 0);
      const corpusComplete = artCorpusTotal > 0 && artTerminal >= artCorpusTotal;
      const pending = corpusComplete
        ? 0
        : (withoutCover > 0 ? withoutCover : Math.max(0, total - withCover));
      if (pending <= 0 && (withCover >= total || corpusComplete)) return;
      startBackgroundCoverEnrichment(
        albumCountHint > 0 ? albumCountHint : (artCorpusTotal > 0 ? artCorpusTotal : total),
      );
    } catch { /* best-effort — probe retries */ }
  })();
}

export function startEnrichmentProgressProbe(): void {
  if (enrichmentProbeTimer) return;
  if (typeof window === 'undefined' || !('__TAURI__' in window)) return;
  enrichmentProbeTimer = setInterval(() => {
    if (isLibraryScanInProgress()) return;
    ensureEnrichmentProgressWatch();
  }, ENRICHMENT_PROBE_MS);
}

export function stopEnrichmentProgressProbe(): void {
  if (!enrichmentProbeTimer) return;
  clearInterval(enrichmentProbeTimer);
  enrichmentProbeTimer = null;
}

function startArtworkProgressWatch(operationId: string, albumCountHint: number): void {
  artworkPhaseFinished = false;
  if (artworkWatchUnsub) {
    artworkWatchUnsub();
    artworkWatchUnsub = null;
  }
  if (artworkIdleTimer) {
    clearTimeout(artworkIdleTimer);
    artworkIdleTimer = null;
  }
  if (coverStatsPollTimer) {
    clearInterval(coverStatsPollTimer);
    coverStatsPollTimer = null;
  }
  resetArtworkProgress();
  lastArtworkSnap = { total: 0, withArtwork: 0, exhausted: 0 };
  lastCoverStatsWithCover = 0;
  backgroundCoverOperationId = operationId;

  void (async () => {
    let albums: Array<Record<string, unknown>> = [];
    try {
      const resp = await libraryService.getAlbums({
        limit: ALBUM_INDEX_FIRST_CHUNK,
        offset: 0,
      });
      albums = (resp?.items || resp?.albums || []) as Array<Record<string, unknown>>;
    } catch { /* best-effort — pub/sub still tracks new resolves */ }

    if (artworkPhaseFinished) return;

    beginArtworkProgress(albums, albumCountHint);
    const initial = getArtworkProgress();
    lastArtworkSnap = {
      total: initial.total,
      withArtwork: initial.withArtwork,
      exhausted: initial.exhausted ?? 0,
    };
    onArtworkProgressTick(operationId, initial);
    scheduleArtworkIdleCheck(operationId);

    artworkWatchUnsub = subscribeArtworkProgress((snap) => {
      onArtworkProgressTick(operationId, snap);
    });

    const pollCoverStats = async () => {
      if (artworkPhaseFinished) return;
      try {
        const pub = getArtworkProgress();
        const resp = await libraryService.getCoverStats();
        if (artworkPhaseFinished) return;
        const artCorpusTotal = Number(resp?.artCorpusTotal ?? resp?.art_corpus_total ?? 0);
        const artTerminal = Number(resp?.artTerminal ?? resp?.art_terminal ?? 0);
        const wallTotal = Number(resp?.total ?? 0);
        const total = artCorpusTotal > 0
          ? Math.max(pub.total, artCorpusTotal)
          : Math.max(pub.total, wallTotal);
        const serverWith = Number(resp?.withCover ?? resp?.embedded ?? 0);
        const placeholderTerminal = Number(resp?.placeholderTerminal ?? resp?.placeholder_terminal ?? 0);
        const withArtwork = Math.max(
          pub.withArtwork,
          serverWith,
          artTerminal > 0 ? Math.max(0, artTerminal - placeholderTerminal) : 0,
        );
        const exhausted = Math.max(
          pub.exhausted ?? 0,
          placeholderTerminal,
          artCorpusTotal > 0 && artTerminal >= artCorpusTotal
            ? Math.max(0, total - withArtwork - (pub.exhausted ?? 0))
            : 0,
        );
        if (artCorpusTotal > 0 && artTerminal >= artCorpusTotal) {
          onArtworkProgressTick(operationId, {
            total,
            withArtwork: Math.max(withArtwork, artTerminal - placeholderTerminal),
            exhausted: Math.max(exhausted, placeholderTerminal),
            pending: 0,
            missing: 0,
          });
          return;
        }
        if (serverWith > lastCoverStatsWithCover) {
          lastCoverStatsWithCover = serverWith;
          const now = Date.now();
          if (
            !artworkPhaseFinished
            && now - lastEnrichmentCatalogEventAt >= ENRICHMENT_CATALOG_EVENT_MIN_MS
          ) {
            lastEnrichmentCatalogEventAt = now;
            try {
              window.dispatchEvent(new CustomEvent('maestro:library:changed', {
                detail: {
                  reason: 'connector_catalog_changed',
                  catalogReason: 'enrichment_cover',
                },
              }));
            } catch { /* ignore */ }
          }
        }
        onArtworkProgressTick(operationId, {
          total,
          withArtwork,
          exhausted,
          pending: Math.max(0, total - withArtwork - exhausted),
          missing: Math.max(0, total - withArtwork - exhausted),
        });
      } catch { /* best-effort — pub/sub still tracks resolves */ }
    };
    void pollCoverStats();
    coverStatsPollTimer = setInterval(() => { void pollCoverStats(); }, 2000);
  })();
}

export type ScanPhase =
  | 'idle'
  | 'scanning'
  | 'ingesting'
  | 'finalizing'
  | 'done'
  | 'cancelled'
  | 'error';

export interface ScanProgress {
  files_found?: number;
  albums_identified?: number;
  percent?: number;
  eta_seconds?: number;
  current_file?: string;
  phase?: string;
}

export interface ScanSource {
  id: string;
  /** Registered library source root path. */
  path: string;
  /** When set, only walk/tag files under this subfolder (must be inside `path`). */
  scan_path?: string;
}

export interface ScanState {
  phase: ScanPhase;
  selectedPaths: string[];
  scanSources?: ScanSource[];
  machineId?: string;
  nativeProgress: ScanProgress | null;
  ingestTotal: number;
  ingestDone: number;
  trackCount: number;
  albumCount: number;
  error: string | null;
  active: boolean;
}

type Listener = (state: ScanState) => void;

const INGEST_BATCH_SIZE = 200;

const INITIAL_STATE: ScanState = {
  phase: 'idle',
  selectedPaths: [],
  machineId: undefined,
  nativeProgress: null,
  ingestTotal: 0,
  ingestDone: 0,
  trackCount: 0,
  albumCount: 0,
  error: null,
  active: false,
};

let state: ScanState = { ...INITIAL_STATE };
const listeners = new Set<Listener>();
let unlistenNative: (() => void) | null = null;
let unlistenBatch: (() => void) | null = null;
let currentRun: Promise<void> | null = null;
let cancelRequested = false;
/** Snapshot of sources for the active run — avoids stale reads from `state`. */
let activeRunScanSources: ScanSource[] | undefined;

// Tracks ingested from streaming batches — count maintained across the scan
let streamIngestedCount = 0;
let scanOperationId: string | null = null;
let unlistenLocalProgress: (() => void) | null = null;
/** Adopts connector-native walks when the web coordinator did not start the scan (reload, boot). */
let globalProgressUnlisten: (() => void) | null = null;
let globalProgressInitStarted = false;
let adoptedScanStartedEventFired = false;
/** True when local_library_get_scan_walk_progress reports an active connector walk. */
let connectorWalkScanActive = false;
let connectorWalkPollTimer: ReturnType<typeof setInterval> | null = null;
let connectorWalkPollAlbumsEmpty = false;
const CONNECTOR_WALK_POLL_MS_EMPTY = 2000;
const CONNECTOR_WALK_POLL_MS_ACTIVE = 1500;

export type ConnectorScanWalkProgress = {
  phase: string;
  entries_seen: number;
  audio_files: number;
  iso_skipped: number;
  elapsed_secs: number;
  current_path: string;
  scan_in_progress: boolean;
};

function walkProgressToNativePayload(p: ConnectorScanWalkProgress): Record<string, unknown> {
  const phase = String(p.phase || 'walk');
  const entries = Number(p.entries_seen) || 0;
  const audio = Number(p.audio_files) || 0;
  return {
    phase,
    walk_phase: phase,
    entries_seen: entries,
    audio_files: audio,
    iso_skipped: Number(p.iso_skipped) || 0,
    elapsed_secs: Number(p.elapsed_secs) || 0,
    current_path: String(p.current_path || ''),
    current_file: entries,
    files_found: audio || entries,
  };
}

async function invokeConnectorScanWalkProgress(): Promise<ConnectorScanWalkProgress | null> {
  if (!isTauriConnector()) return null;
  try {
    const tauri = (window as Window & { __TAURI__?: { core?: { invoke?: (cmd: string) => Promise<unknown> } } }).__TAURI__;
    const payload = await tauri?.core?.invoke?.('local_library_get_scan_walk_progress');
    if (!payload || typeof payload !== 'object') return null;
    const p = payload as Record<string, unknown>;
    return {
      phase: String(p.phase ?? ''),
      entries_seen: Number(p.entries_seen ?? 0),
      audio_files: Number(p.audio_files ?? 0),
      iso_skipped: Number(p.iso_skipped ?? 0),
      elapsed_secs: Number(p.elapsed_secs ?? 0),
      current_path: String(p.current_path ?? ''),
      scan_in_progress: p.scan_in_progress === true,
    };
  } catch (e) {
    console.debug('[scan-coordinator] walk progress poll failed:', e);
    return null;
  }
}

function syncConnectorScanWalkPolling(): void {
  const shouldPoll = isTauriConnector()
    && (connectorWalkPollAlbumsEmpty || connectorWalkScanActive || isLibraryScanInProgressInternal());
  if (shouldPoll && !connectorWalkPollTimer) {
    void refreshConnectorScanWalkProgress();
    const intervalMs = connectorWalkScanActive || isLibraryScanInProgressInternal()
      ? CONNECTOR_WALK_POLL_MS_ACTIVE
      : CONNECTOR_WALK_POLL_MS_EMPTY;
    connectorWalkPollTimer = setInterval(() => {
      void refreshConnectorScanWalkProgress();
    }, intervalMs);
  } else if (!shouldPoll && connectorWalkPollTimer) {
    clearInterval(connectorWalkPollTimer);
    connectorWalkPollTimer = null;
  }
}

/** Poll connector walk truth and merge into coordinator scan state (boot/rescan adoption). */
export async function refreshConnectorScanWalkProgress(): Promise<boolean> {
  const walk = await invokeConnectorScanWalkProgress();
  if (!walk) return false;

  if (walk.scan_in_progress) {
    if (localScanUiCompleted || !connectorWalkScanActive) {
      resetLocalScanUiCompleted();
    }
    connectorWalkScanActive = true;
    if (!currentRun) {
      adoptConnectorScanProgress(walkProgressToNativePayload(walk));
    } else {
      bumpScanDisplayPeaks(walkProgressToNativePayload(walk), state);
      setState({
        phase: 'scanning',
        active: true,
        nativeProgress: walkProgressToNativePayload(walk) as ScanProgress,
      });
    }
    syncConnectorScanWalkPolling();
    return true;
  }

  if (connectorWalkScanActive) {
    connectorWalkScanActive = false;
    syncConnectorScanWalkPolling();
  }
  return false;
}

/** Keep polling while the wall is empty or a connector walk is active. */
export function setConnectorScanWalkPollContext(albumsEmpty: boolean): void {
  connectorWalkPollAlbumsEmpty = albumsEmpty;
  syncConnectorScanWalkPolling();
}

export function ensureConnectorScanWalkPolling(): void {
  syncConnectorScanWalkPolling();
}

export function stopConnectorScanWalkPolling(): void {
  connectorWalkPollAlbumsEmpty = false;
  if (connectorWalkPollTimer) {
    clearInterval(connectorWalkPollTimer);
    connectorWalkPollTimer = null;
  }
}

export function isConnectorWalkScanActive(): boolean {
  return connectorWalkScanActive;
}

/** @internal Test hook — simulate connector walk poll payload. */
export function __applyConnectorWalkProgressForTest(
  walk: ConnectorScanWalkProgress,
): void {
  if (walk.scan_in_progress) {
    connectorWalkScanActive = true;
    resetLocalScanUiCompleted();
    adoptConnectorScanProgress(walkProgressToNativePayload(walk));
  } else {
    connectorWalkScanActive = false;
  }
}

interface ActiveScanTiming {
  baselineKey: string;
  walkStartedAt: number;
  tagPhaseStartedAt: number;
  walkEntriesPeak: number;
  lastPhase: string;
}
let activeScanTiming: ActiveScanTiming | null = null;

function resolveScanBaselineKey(opts: {
  selectedPaths: string[];
  scanSources?: ScanSource[];
  prebuiltSources?: ScanSource[];
}): string {
  const src = opts.prebuiltSources?.[0] ?? opts.scanSources?.[0];
  if (src) return src.scan_path || src.path;
  return opts.selectedPaths[0] ?? '';
}

function isTauriConnector(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as Window & { __TAURI__?: unknown; __TAURI_INTERNALS__?: unknown };
  return !!(w.__TAURI__ || w.__TAURI_INTERNALS__);
}

function emit(): void {
  for (const l of listeners) {
    try { l(state); } catch { /* listener errors must not break the scan */ }
  }
}

function setState(patch: Partial<ScanState>): void {
  state = { ...state, ...patch };
  emit();
}

export function subscribeScan(listener: Listener): () => void {
  ensureGlobalScanProgressListener();
  listeners.add(listener);
  try { listener(state); } catch {}
  return () => { listeners.delete(listener); };
}

export function getScanState(): ScanState {
  return state;
}

export function isScanActive(): boolean {
  return state.active;
}

/** True while a library scan is running or finishing — broader than `state.active` alone. */
function isLibraryScanInProgressInternal(): boolean {
  if (state.active) return true;
  if (connectorWalkScanActive) return true;
  if (scanOperationId != null && !localScanUiCompleted) return true;
  if (state.phase === 'scanning' || state.phase === 'ingesting' || state.phase === 'finalizing') {
    return true;
  }
  const nativePhase = String(state.nativeProgress?.phase ?? '');
  return [
    'walk',
    'enumerating',
    'discovering',
    'reconciling',
    'tag_read',
    'scanning',
    'finalize',
    'finalizing',
  ].includes(nativePhase);
}

export function isLibraryScanInProgress(): boolean {
  return isLibraryScanInProgressInternal();
}

/** Local walk/tag/extract finished — library is usable; network cover fill may continue in background. */
export function isLocalScanPhaseComplete(): boolean {
  return localScanUiCompleted;
}

async function completeLocalScanPhaseAsync(source: 'progress' | 'invoke'): Promise<void> {
  if (localScanUiCompleted || !scanOperationId) return;
  if (source === 'progress') {
    logScanCompletedViaConnector();
  }
  markScanProgressWatchdogComplete();
  localScanUiCompleted = true;

  const albumsHint = getScanAlbumsIndexedCount() || state.albumCount || 0;
  setState({
    phase: 'done',
    active: false,
    albumCount: albumsHint,
  });

  invalidateLibraryStore();
  try {
    window.dispatchEvent(new CustomEvent('maestro:library:changed', {
      detail: { reason: 'scan-complete', forceFullRefresh: true },
    }));
  } catch { /* ignore */ }

  const doneId = scanOperationId;
  const existingOp = useOperationProgressStore.getState().operations[doneId];
  const honestDoneLabel = existingOp?.scanIndexedCount != null
    ? formatScanCompleteSummary(parseHonestScanMetrics({
      indexed_count: existingOp.scanIndexedCount,
      skipped_count: existingOp.scanUnchangedCount ?? 0,
      set_aside: existingOp.scanSetAsideCount
        ? { other: existingOp.scanSetAsideCount }
        : undefined,
      terminal_files: existingOp.terminalFiles ?? undefined,
      walk_files_found: existingOp.walkFilesFound ?? undefined,
    }), scanModeFromProgress(state.nativeProgress as Record<string, unknown> ?? {}))
    : null;
  scanOperationId = null;
  useOperationProgressStore.getState().updateOperation(doneId, {
    label: honestDoneLabel ?? `Scan complete — ${albumsHint} album${albumsHint !== 1 ? 's' : ''}`,
    phase: 'done',
    progress: 1,
  });
  const dismissMs = (existingOp?.scanSetAsideCount ?? 0) > 0 ? 8000 : 3000;
  window.setTimeout(() => {
    useOperationProgressStore.getState().endOperation(doneId);
  }, dismissMs);

  const albums = await fetchRealAlbumCount(albumsHint);
  startBackgroundCoverEnrichment(albums);
  console.log(
    `[scan-coordinator] local scan phase complete (${source}) — ${albums} albums; covers continue in background`,
  );
}

async function completeLocalScanViaWatchdog(snapshot: {
  opId: string;
  phase: string;
  progress: number | null;
  total: number | null;
  silentMs: number;
}): Promise<void> {
  if (localScanUiCompleted || !scanOperationId || scanOperationId !== snapshot.opId) return;
  logScanWatchdogRescue(snapshot);
  markScanProgressWatchdogComplete();
  localScanUiCompleted = true;

  const rescueLabel = buildWatchdogRescueLabel(snapshot.progress, snapshot.total);
  const albumsHint = getScanAlbumsIndexedCount() || state.albumCount || 0;
  setState({
    phase: 'done',
    active: false,
    albumCount: albumsHint,
  });

  invalidateLibraryStore();
  try {
    window.dispatchEvent(new CustomEvent('maestro:library:changed', {
      detail: { reason: 'scan-watchdog', forceFullRefresh: true },
    }));
  } catch { /* ignore */ }

  const doneId = scanOperationId;
  scanOperationId = null;
  useOperationProgressStore.getState().updateOperation(doneId, {
    label: rescueLabel,
    phase: 'done',
    progress: 1,
  });
  window.setTimeout(() => {
    useOperationProgressStore.getState().endOperation(doneId);
  }, 5000);

  const albums = await fetchRealAlbumCount(albumsHint);
  startBackgroundCoverEnrichment(albums);
}

/** Best-effort file count from local-core walk/tag progress (v11.0.60+ fields). */
export function getScanFilesFoundCount(scan: ScanState = state): number {
  const np = scan.nativeProgress as Record<string, unknown> | null;
  let raw = 0;
  if (np) {
    if (typeof np.audio_files === 'number' && np.audio_files > 0) raw = np.audio_files;
    else if (typeof np.entries_seen === 'number' && np.entries_seen > 0) raw = np.entries_seen;
    else if (typeof np.current_file === 'number' && np.current_file > 0) raw = np.current_file;
    else if (typeof np.files_found === 'number' && np.files_found > 0) raw = np.files_found;
  }
  if (raw === 0 && scan.ingestDone > 0) raw = scan.ingestDone;
  return Math.max(scanDisplayFilesPeak, raw);
}

export function getScanAlbumsIndexedCount(scan: ScanState = state): number {
  const np = scan.nativeProgress as Record<string, unknown> | null;
  let raw = 0;
  if (np && typeof np.albums_identified === 'number' && np.albums_identified > 0) {
    raw = np.albums_identified;
  } else if (scan.albumCount > 0) {
    raw = scan.albumCount;
  }
  return Math.max(scanDisplayAlbumsPeak, raw);
}

export async function startScan(opts: {
  selectedPaths: string[];
  scanSources?: ScanSource[];
  /** Sources already registered via local_library_add_source — skip duplicate creation. */
  prebuiltSources?: ScanSource[];
  isTemporary?: boolean;
}): Promise<void> {
  if (currentRun) {
    console.log(`[Scan] scan already in progress, ignoring duplicate request ts=${new Date().toISOString()}`);
    return currentRun;
  }

  await refreshConnectorScanWalkProgress();
  if (connectorWalkScanActive) {
    console.log(
      `[Scan] connector scan already active (catalog_scan_in_progress) — skipping duplicate startScan ts=${new Date().toISOString()}`,
    );
    return Promise.resolve();
  }

  if (typeof window !== 'undefined' && window.__TAURI__) {
    if (isRemoteLibraryViewer()) {
      console.warn('[Scan] blocked — remote library viewer cannot add local sources');
      return Promise.reject(new Error('Add Music is only available on the Mac where your library lives.'));
    }
  }

  const collapsed = collapseBulkScanPlan({
    selectedPaths: opts.selectedPaths,
    scanSources: opts.scanSources,
    prebuiltSources: opts.prebuiltSources,
  });
  opts = { ...opts, ...collapsed };

  const isRescan = !!(opts.scanSources && opts.scanSources.length > 0);
  const prebuiltCount = opts.prebuiltSources?.length || 0;
  console.log(
    `[Scan] ${isRescan ? 're-scan' : 'fresh scan'} requested, current state: ${state.phase}, ` +
    `paths=${opts.selectedPaths.length}, sources=${opts.scanSources?.length || 0}, ` +
    `prebuilt=${prebuiltCount} ts=${new Date().toISOString()}`,
  );

  cancelRequested = false;
  streamIngestedCount = 0;
  resetLocalScanUiCompleted();
  // Bust version immediately so parallel grid fetches cannot cache-hit mid-scan.
  try {
    useLibraryCacheStore.getState().setVersion(null);
  } catch { /* cache store optional */ }
  probeLocalLibraryIpc().then((ipcOk) => {
    if (!ipcOk) return;
    try {
      useLibraryCacheStore.getState().setVersion(null);
      localStorage.removeItem('maestro:libraryCache');
      window.dispatchEvent(new CustomEvent('maestro:library:scan-started'));
    } catch { /* cache store optional */ }
  }).catch(() => {});
  stopArtworkProgressWatch();
  for (const op of Object.values(useOperationProgressStore.getState().operations)) {
    if (op.phase === 'artwork') {
      useOperationProgressStore.getState().endOperation(op.id);
    }
  }
  if (scanOperationId) {
    useOperationProgressStore.getState().endOperation(scanOperationId);
    scanOperationId = null;
  }
  const baselineKey = resolveScanBaselineKey(opts);
  activeScanTiming = {
    baselineKey,
    walkStartedAt: Date.now(),
    tagPhaseStartedAt: 0,
    walkEntriesPeak: 0,
    lastPhase: '',
  };
  const now = Date.now();
  useScanSetAsideUIStore.getState().clearSession();
  let albumsFloor = 0;
  try {
    albumsFloor = getSessionAlbumGridCount();
  } catch { /* cache store optional */ }
  resetScanDisplayPeaks(albumsFloor);
  scanOperationId = useOperationProgressStore.getState().beginOperation(buildInitialScanLabel(opts), {
    progress: 0,
    phase: 'enumerating',
    baselineKey,
    phaseStartedAt: now,
    ...(albumsFloor > 0 ? { scanAlbumsIdentifiedPeak: albumsFloor } : {}),
  });
  resetHonestScanProgressMonotonic(scanOperationId);
  startScanProgressWatchdog(scanOperationId, (snapshot) => {
    void completeLocalScanViaWatchdog(snapshot);
  });
  const initialSources = opts.prebuiltSources?.length
    ? opts.prebuiltSources
    : opts.scanSources;
  activeRunScanSources = initialSources?.length ? [...initialSources] : undefined;
  state = {
    ...INITIAL_STATE,
    phase: 'scanning',
    selectedPaths: opts.selectedPaths,
    scanSources: initialSources,
    active: true,
  };
  emit();

  currentRun = runScan().finally(() => {
    currentRun = null;
    activeRunScanSources = undefined;
  });
  return currentRun;
}

/** @deprecated import from scanProgressTruth */
export { walkTracksFoundFromProgress } from './scanProgressTruth';

function applyLocalProgressToOperation(p: Record<string, unknown>): void {
  if (!scanOperationId || localScanUiCompleted) return;
  noteScanProgressWatchdog(p, scanOperationId);
  bumpScanDisplayPeaks(p, state);
  const phase = String(p.phase ?? '');
  const metrics = enrichHonestScanMetrics(scanOperationId, p, parseHonestScanMetrics(p));
  const honestStatusLine = formatScanningStatusLine(metrics, phase);
  const walkRunningTotal = metrics.walkRunningTotal;
  const walkTracksFound = walkTracksFoundFromProgress(p);
  const rawCurrent = typeof p.current_file === 'number' ? p.current_file : null;
  const rawTotal = typeof p.total_files === 'number' && p.total_files > 0 ? p.total_files : null;
  const total = rawTotal != null ? Math.max(rawTotal, rawCurrent ?? 0) : null;
  const current = rawCurrent != null && total != null
    ? Math.min(Math.max(0, rawCurrent), total)
    : rawCurrent;
  const pathHint = typeof p.current_path === 'string' ? p.current_path : null;
  const sourcesTotal = metrics.sourcesTotal ?? (typeof p.sources_total === 'number' ? p.sources_total : null);
  const rawSourceIndex = typeof p.source_index === 'number' ? p.source_index : null;
  const isTagLikePhase = phase === 'tag_read' || phase === 'scanning';
  const sourceIndex = (isTagLikePhase && sourcesTotal != null && rawSourceIndex === sourcesTotal)
    ? (metrics.activeSourceIndex ?? rawSourceIndex)
    : rawSourceIndex;
  const multiSource = metrics.interleavedMultiSource
    || (sourcesTotal != null && sourcesTotal > 1 && sourceIndex != null);
  const isWalkLikePhase = phase === 'walk'
    || phase === 'enumerating'
    || phase === 'discovering'
    || phase === 'reconciling';
  const activeFolderIndex = metrics.activeSourceIndex ?? sourceIndex;
  const multiSourceFolderHint = multiSource && activeFolderIndex != null && sourcesTotal != null
    ? `Folder ${activeFolderIndex} of ${sourcesTotal}`
    : null;
  const folderHint = isWalkLikePhase ? multiSourceFolderHint : null;
  const etaMs = typeof p.eta_ms === 'number' && p.eta_ms >= 0 ? p.eta_ms : null;
  const filesPerSecond = metrics.smoothedTagFps
    ?? (typeof p.files_per_second === 'number' && p.files_per_second > 0 ? p.files_per_second : null);

  const existing = useOperationProgressStore.getState().operations[scanOperationId];

  if (activeScanTiming) {
    const walkPeak = walkRunningTotal ?? walkTracksFound ?? current;
    if (isWalkLikePhase && typeof walkPeak === 'number') {
      activeScanTiming.walkEntriesPeak = Math.max(activeScanTiming.walkEntriesPeak, walkPeak);
    }
    if (phase === 'tag_read' && activeScanTiming.lastPhase !== 'tag_read') {
      activeScanTiming.tagPhaseStartedAt = Date.now();
    }
    if (phase === 'scanning' && activeScanTiming.lastPhase !== 'scanning') {
      activeScanTiming.tagPhaseStartedAt = Date.now();
    }
    activeScanTiming.lastPhase = phase;
  }

  let label = 'Preparing scan…';
  let progress: number | undefined;
  let totalSteps: number | undefined;
  let currentStep: number | undefined;
  let walkFileCount: number | undefined;
  let opPhase = phase || 'scanning';

  const isFolderWalkTerminal = (phase === 'done' && p.walk_finished === true) || phase === 'walk_complete';
  const moreFoldersAfterWalk = multiSource
    && activeFolderIndex != null
    && sourcesTotal != null
    && activeFolderIndex < sourcesTotal;

  if (multiSource && sourcesTotal != null) {
    totalSteps = sourcesTotal;
    currentStep = activeFolderIndex ?? metrics.sourcesCompleted ?? existing?.currentStep ?? 1;
  }

  const aggregateLine = metrics.aggregateStatusLine;
  const aggregatePhase = metrics.aggregatePhase;

  if (phase === 'walk' || phase === 'enumerating' || phase === 'discovering') {
    const displayCount = walkRunningTotal ?? walkTracksFound ?? current;
    label = aggregateLine ?? (folderHint
      ? `${folderHint} — ${honestStatusLine ?? 'finding files…'}`
      : honestStatusLine ?? (displayCount != null && displayCount > 0
        ? `Scanning — finding files… ${displayCount.toLocaleString()} found`
        : 'Scanning — finding files…'));
    if (displayCount != null && displayCount > 0) {
      walkFileCount = displayCount;
      if (!multiSource) currentStep = displayCount;
    }
    opPhase = 'enumerating';
  } else if (phase === 'reconciling' || (phase === 'walk' && p.walk_subphase === 'reconcile')) {
    const displayCount = walkRunningTotal ?? walkTracksFound ?? current;
    label = aggregateLine ?? (folderHint
      ? `${folderHint} — reconciling…`
      : honestStatusLine ?? (displayCount != null && displayCount > 0
        ? `Scanning — finding files… ${displayCount.toLocaleString()} found`
        : 'Scanning — finding files…'));
    if (displayCount != null && displayCount > 0) {
      walkFileCount = displayCount;
      if (!multiSource) currentStep = displayCount;
    }
    opPhase = multiSource ? 'enumerating' : 'reconciling';
  } else if (phase === 'walk_complete' || isFolderWalkTerminal) {
    const skippedCount = typeof p.skipped_count === 'number' ? p.skipped_count : null;
    const filesFound = current ?? (typeof p.total_files === 'number' && p.total_files > 0 ? p.total_files : null);
    const scanMode = scanModeFromProgress(p);
    if (
      scanMode === 'incremental'
      && skippedCount != null && filesFound != null && skippedCount > 0
    ) {
      label = `${skippedCount.toLocaleString()} file${skippedCount === 1 ? '' : 's'} already up to date`;
      opPhase = 'walk_complete';
      progress = multiSource ? progress : 0.85;
    } else if (
      !hasScanProgressContract(p)
      && skippedCount != null && filesFound != null && skippedCount > 0
      && skippedCount === filesFound
    ) {
      label = `${skippedCount.toLocaleString()} file${skippedCount === 1 ? '' : 's'} already up to date`;
      opPhase = 'walk_complete';
      progress = multiSource ? progress : 0.85;
    } else if (moreFoldersAfterWalk && multiSourceFolderHint) {
      label = `${multiSourceFolderHint} — folder walk complete`;
      if (filesFound != null && filesFound > 0) walkFileCount = filesFound;
      opPhase = 'enumerating';
    } else {
      label = current != null && current > 0
        ? `Walk complete — ${current.toLocaleString()} audio file${current === 1 ? '' : 's'} indexed`
        : 'Walk complete — reading tags…';
      opPhase = 'walk_complete';
      progress = multiSource ? progress : 0.85;
    }
  } else if (phase === 'tag_read' || phase === 'scanning') {
    if (aggregateLine) {
      label = aggregateLine;
      opPhase = 'scanning';
    } else if (honestStatusLine) {
      label = honestStatusLine;
      opPhase = 'scanning';
    } else {
      const tagFolderHint = multiSource && sourcesTotal != null && activeFolderIndex != null
        ? `folder ${activeFolderIndex}/${sourcesTotal}`
        : null;
      label = current != null
        ? `${tagFolderHint ? `${tagFolderHint} — ` : ''}Reading tags… ${formatTagReadProgress(current, total, total != null && total <= current)}`
        : tagFolderHint
          ? `Reading tags (${tagFolderHint})…`
          : 'Reading tags from files (embedded metadata first)…';
      opPhase = 'scanning';
    }
    const honestProgress = computeHonestScanProgress(scanOperationId, metrics, opPhase);
    if (honestProgress != null) {
      progress = honestProgress;
    } else if (!multiSource && total != null && current != null) {
      progress = total > 0 ? Math.min(1, current / total) : undefined;
      totalSteps = total > current ? total : undefined;
      currentStep = current;
    }
  } else if (phase === 'finalize') {
    const finalizeStage = typeof p.finalize_stage === 'string' ? p.finalize_stage : null;
    const stageLabel = finalizeStageDisplayLabel(finalizeStage);
    label = stageLabel ?? aggregateLine ?? honestStatusLine ?? 'Scanning — finalizing…';
    progress = computeHonestScanProgress(scanOperationId, metrics, 'finalize') ?? 0.98;
    opPhase = 'finalizing';
  } else if (phase === 'scan_complete') {
    label = formatScanCompleteSummary(metrics, scanModeFromProgress(p));
    progress = 1;
    opPhase = 'done';
    if (metrics.setAsideTotal > 0 && scanOperationId) {
      useScanSetAsideUIStore.getState().setSessionFromScanComplete({
        operationId: scanOperationId,
        indexed: metrics.indexedCount ?? metrics.terminalFiles ?? metrics.walkFilesFound ?? 0,
        setAside: metrics.setAsideTotal,
        unchanged: metrics.unchangedCount ?? 0,
        completedAt: Date.now(),
      });
    }
  } else if (phase === 'done' && p.walk_finished !== true && !hasScanProgressContract(p)) {
    label = formatScanCompleteSummary(metrics);
    progress = 1;
    opPhase = 'done';
    if (metrics.setAsideTotal > 0 && scanOperationId) {
      useScanSetAsideUIStore.getState().setSessionFromScanComplete({
        operationId: scanOperationId,
        indexed: metrics.indexedCount ?? metrics.terminalFiles ?? metrics.walkFilesFound ?? 0,
        setAside: metrics.setAsideTotal,
        unchanged: metrics.unchangedCount ?? 0,
        completedAt: Date.now(),
      });
    }
  } else if (phase === 'cancelled') {
    label = 'Scan cancelled';
    opPhase = 'cancelled';
    progress = 1;
  } else if (phase === 'error') {
    const filesFound = typeof p.total_files === 'number' && p.total_files > 0 ? p.total_files : null;
    const skippedCount = typeof p.skipped_count === 'number' ? p.skipped_count : null;
    const jobsQueued = typeof p.jobs_queued === 'number' ? p.jobs_queued : null;
    if (
      jobsQueued === 0
      && filesFound != null
      && skippedCount != null
      && skippedCount === filesFound
    ) {
      label = `${skippedCount.toLocaleString()} file${skippedCount === 1 ? '' : 's'} already up to date`;
      opPhase = 'walk_complete';
      progress = multiSource ? progress : 0.85;
    } else {
      label = typeof p.error === 'string' ? `Scan error: ${p.error}` : 'Scan error';
      opPhase = 'error';
      progress = 1;
    }
  }

  const elapsedMs = typeof p.elapsed_ms === 'number' && p.elapsed_ms >= 0 ? p.elapsed_ms : undefined;

  if (progress == null && aggregatePhase !== 'idle') {
    progress = computeHonestScanProgress(scanOperationId, metrics, opPhase);
  }

  if (aggregatePhase === 'reading_tags') opPhase = 'scanning';
  else if (aggregatePhase === 'walking') opPhase = 'enumerating';
  else if (aggregatePhase === 'finishing') opPhase = 'finalizing';

  const aggPhaseChanged = existing?.scanAggregatePhase !== aggregatePhase;
  const phaseChanged = aggPhaseChanged || existing?.phase !== opPhase;

  const finalizeStage = phase === 'finalize' && typeof p.finalize_stage === 'string'
    ? p.finalize_stage
    : (phase === 'finalizing' || opPhase === 'finalizing' ? existing?.scanFinalizeStage ?? null : null);

  useOperationProgressStore.getState().updateOperation(scanOperationId, {
    label,
    phase: opPhase,
    progress,
    totalSteps,
    currentStep,
    ...(walkFileCount != null ? { walkFileCount } : {}),
    ...(phase === 'tag_read' || phase === 'scanning' ? { walkFileCount: null } : {}),
    currentLabel: pathHint,
    etaMs: etaMs ?? undefined,
    elapsedMs,
    filesPerSecond: filesPerSecond ?? undefined,
    baselineKey: activeScanTiming?.baselineKey,
    phaseStartedAt: aggPhaseChanged ? Date.now() : existing?.phaseStartedAt,
    walkFilesFound: metrics.globalWalkFilesFound ?? metrics.walkFilesFound,
    terminalFiles: metrics.globalTerminalFiles ?? metrics.terminalFiles,
    pendingJobs: metrics.pendingJobs,
    scanSetAsideCount: metrics.setAsideTotal > 0 ? metrics.setAsideTotal : null,
    scanIndexedCount: metrics.indexedCount,
    scanUnchangedCount: metrics.unchangedCount,
    scanStatusLine: finalizeStageDisplayLabel(finalizeStage) ?? aggregateLine ?? honestStatusLine,
    walkRunningTotal: walkRunningTotal ?? undefined,
    scanAlbumsIdentifiedPeak: scanDisplayAlbumsPeak > 0 ? scanDisplayAlbumsPeak : undefined,
    scanAggregatePhase: aggregatePhase,
    scanSourcesCompleted: metrics.sourcesCompleted ?? undefined,
    scanSourcesTotal: metrics.sourcesTotal ?? undefined,
    scanInterleavedMultiSource: metrics.interleavedMultiSource || undefined,
    scanGlobalDirectTotals: metrics.globalDirectTotals || undefined,
    scanWalkStillRunning: metrics.walkStillRunning || undefined,
    globalEtaEstimating: metrics.globalEtaEstimating || undefined,
    globalEtaSec: metrics.globalEtaSec ?? undefined,
    scanFinalizeStage: finalizeStage ?? undefined,
  });

  if (currentRun && shouldCompleteLocalScanPhase(p)) {
    void completeLocalScanPhaseAsync('progress');
  } else if (isScanProgressTerminalEvent(p) && String(p.phase ?? '') !== 'scan_complete') {
    markScanProgressWatchdogComplete();
    const terminalPhase = String(p.phase ?? '');
    if (terminalPhase === 'cancelled') {
      localScanUiCompleted = true;
      setState({ phase: 'cancelled', active: false });
    } else if (terminalPhase === 'error') {
      localScanUiCompleted = true;
      setState({
        phase: 'error',
        active: false,
        error: typeof p.error === 'string' ? p.error : 'Scan failed',
      });
    }
    if (scanOperationId) {
      const terminalId = scanOperationId;
      scanOperationId = null;
      window.setTimeout(() => {
        useOperationProgressStore.getState().endOperation(terminalId);
      }, terminalPhase === 'cancelled' ? 3000 : 4000);
    }
  }
}

function isConnectorProgressInFlight(phase: string, p: Record<string, unknown>): boolean {
  if (['walk', 'enumerating', 'discovering', 'reconciling', 'tag_read', 'scanning', 'finalize', 'scan_complete'].includes(phase)) {
    return true;
  }
  if (phase === 'walk_complete') return true;
  if (phase === 'done' && p.walk_finished === true) return true;
  return false;
}

function isConnectorProgressTerminal(phase: string, p: Record<string, unknown>): boolean {
  if (hasScanProgressContract(p)) {
    return isScanProgressTerminalEvent(p);
  }
  if (phase === 'cancelled') return true;
  if (phase === 'done' && p.walk_finished !== true) return true;
  if (phase === 'error') {
    const filesFound = typeof p.total_files === 'number' && p.total_files > 0 ? p.total_files : null;
    const skippedCount = typeof p.skipped_count === 'number' ? p.skipped_count : null;
    const jobsQueued = typeof p.jobs_queued === 'number' ? p.jobs_queued : null;
    if (jobsQueued === 0 && filesFound != null && skippedCount != null && skippedCount === filesFound) {
      return false;
    }
    return true;
  }
  return false;
}

function adoptConnectorScanProgress(p: Record<string, unknown>): void {
  if (!scanOperationId) {
    let albumsFloor = 0;
    try { albumsFloor = getSessionAlbumGridCount(); } catch { /* optional */ }
    resetScanDisplayPeaks(albumsFloor);
    const now = Date.now();
    scanOperationId = useOperationProgressStore.getState().beginOperation(
      buildInitialScanLabel({ selectedPaths: [], scanSources: [] }),
      {
        progress: 0,
        phase: 'enumerating',
        phaseStartedAt: now,
        ...(albumsFloor > 0 ? { scanAlbumsIdentifiedPeak: albumsFloor } : {}),
      },
    );
    resetHonestScanProgressMonotonic(scanOperationId);
    startScanProgressWatchdog(scanOperationId, (snapshot) => {
      void completeLocalScanViaWatchdog(snapshot);
    });
    activeScanTiming = {
      baselineKey: 'connector-adopted',
      walkStartedAt: now,
      tagPhaseStartedAt: 0,
      walkEntriesPeak: 0,
      lastPhase: '',
    };
    if (!adoptedScanStartedEventFired) {
      adoptedScanStartedEventFired = true;
      try {
        window.dispatchEvent(new CustomEvent('maestro:library:scan-started'));
      } catch { /* ignore */ }
    }
  }
  bumpScanDisplayPeaks(p, state);
  setState({
    phase: 'scanning',
    active: true,
    nativeProgress: p as ScanProgress,
  });
  applyLocalProgressToOperation(p);
}

async function finishAdoptedScan(): Promise<void> {
  adoptedScanStartedEventFired = false;
  activeScanTiming = null;
  if (localScanUiCompleted) return;
  await completeLocalScanPhaseAsync('progress');
}

function handleGlobalScanProgress(p: Record<string, unknown>): void {
  if (currentRun) return;

  const phase = String(p.phase ?? '');

  // Connector idle-cancel (#070): no orchestrator running — clear phantom scan UI.
  if (!state.active && isConnectorProgressTerminal(phase, p) && phase === 'cancelled') {
    localScanUiCompleted = true;
    markScanProgressWatchdogComplete();
    if (scanOperationId) {
      const terminalId = scanOperationId;
      useOperationProgressStore.getState().updateOperation(terminalId, {
        label: 'Scan cancelled',
        phase: 'cancelled',
        progress: 1,
      });
      scanOperationId = null;
      window.setTimeout(() => {
        useOperationProgressStore.getState().endOperation(terminalId);
      }, 3000);
    }
    setState({ phase: 'cancelled', active: false, nativeProgress: p as ScanProgress });
    return;
  }

  if (localScanUiCompleted) return;

  if (state.active && isConnectorProgressTerminal(phase, p)) {
    bumpScanDisplayPeaks(p, state);
    setState({ nativeProgress: p as ScanProgress });
    applyLocalProgressToOperation(p);
    if (phase === 'scan_complete' && p.terminal === true) {
      void finishAdoptedScan();
      return;
    }
    setState({
      phase: phase === 'cancelled' ? 'cancelled' : phase === 'error' ? 'error' : 'done',
      active: false,
    });
    return;
  }

  if (!state.active) {
    if (!isConnectorProgressInFlight(phase, p)) return;
    adoptConnectorScanProgress(p);
    return;
  }

  bumpScanDisplayPeaks(p, state);
  setState({ nativeProgress: p as ScanProgress });
  applyLocalProgressToOperation(p);
}

/** @internal Simulate connector scan-progress for unit tests. */
export function __simulateConnectorScanProgress(p: Record<string, unknown>): void {
  handleGlobalScanProgress(p);
}

/** Listen for connector walk/tag progress even when this tab did not call startScan(). */
export function ensureGlobalScanProgressListener(): void {
  if (globalProgressInitStarted || !isTauriConnector()) return;
  globalProgressInitStarted = true;
  void onLocalScanProgress(handleGlobalScanProgress)
    .then((unlisten) => { globalProgressUnlisten = unlisten; })
    .catch((e) => {
      console.warn('[scan-coordinator] global progress listener failed:', e);
      globalProgressInitStarted = false;
    });
}

async function finishScanSuccess(
  summary: { total_tracks: number; total_albums?: number },
  ingestFailed = false,
  opts?: { localCore?: boolean },
): Promise<void> {
  const localCore = opts?.localCore === true;

  if (localScanUiCompleted) {
    if (activeScanTiming?.baselineKey) {
      const now = Date.now();
      const tagStarted = activeScanTiming.tagPhaseStartedAt;
      saveScanBaseline(activeScanTiming.baselineKey, {
        walkEntries: activeScanTiming.walkEntriesPeak,
        tagFiles: summary.total_tracks,
        walkMs: tagStarted > activeScanTiming.walkStartedAt
          ? tagStarted - activeScanTiming.walkStartedAt
          : now - activeScanTiming.walkStartedAt,
        tagMs: tagStarted > 0 ? now - tagStarted : 0,
      });
      activeScanTiming = null;
    }
    setState({ trackCount: summary.total_tracks });
    void setupPostScanRemoteAccess(localCore).catch((e) => {
      console.debug('[scan-coordinator] post-scan remote access setup failed:', e);
    });
    return;
  }

  if (!localCore) {
    setState({ phase: 'finalizing' });
    if (scanOperationId) {
      useOperationProgressStore.getState().updateOperation(scanOperationId, {
        label: 'Updating album library…',
        phase: 'finalizing',
        progress: 0.95,
      });
    }
  } else {
    setState({ phase: 'finalizing' });
  }

  const albums = await fetchRealAlbumCount(summary.total_albums ?? 0);

  if (!localCore) {
    try {
      if (scanOperationId) {
        useOperationProgressStore.getState().updateOperation(scanOperationId, {
          label: 'Filling in missing artwork and credits (only where tags are empty)…',
          phase: 'finalizing',
          progress: 0.97,
        });
      }
      const mod = await import('../store/maestroStore');
      mod.useMaestroStore.getState().triggerEnrichment?.();
    } catch { /* enrichment is optional */ }
  }

  invalidateLibraryStore();

  const doneLabel = ingestFailed
    ? `Scan finished with errors — ${albums} albums, ${summary.total_tracks} tracks (some uploads failed)`
    : `Scan complete — ${albums} album${albums !== 1 ? 's' : ''}, ${summary.total_tracks.toLocaleString()} tracks`;

  if (activeScanTiming?.baselineKey) {
    const now = Date.now();
    const tagStarted = activeScanTiming.tagPhaseStartedAt;
    saveScanBaseline(activeScanTiming.baselineKey, {
      walkEntries: activeScanTiming.walkEntriesPeak,
      tagFiles: summary.total_tracks,
      walkMs: tagStarted > activeScanTiming.walkStartedAt
        ? tagStarted - activeScanTiming.walkStartedAt
        : now - activeScanTiming.walkStartedAt,
      tagMs: tagStarted > 0 ? now - tagStarted : 0,
    });
    activeScanTiming = null;
  }

  localScanUiCompleted = true;

  // Clear active BEFORE grid reload — loadAlbums() defers while isScanActive().
  setState({
    phase: 'done',
    trackCount: summary.total_tracks,
    albumCount: albums,
    active: false,
  });
  markScanProgressWatchdogComplete();

  // Brief pause so SQLite handles are released before the grid reads albums.
  await new Promise((r) => setTimeout(r, localCore ? 200 : 500));
  try {
    window.dispatchEvent(new CustomEvent('maestro:library:changed', {
      detail: { reason: 'scan-complete', forceFullRefresh: true },
    }));
  } catch { /* ignore */ }

  if (scanOperationId) {
    const doneId = scanOperationId;
    const existingOp = useOperationProgressStore.getState().operations[doneId];
    const honestDoneLabel = existingOp?.scanIndexedCount != null
      ? formatScanCompleteSummary(parseHonestScanMetrics({
        indexed_count: existingOp.scanIndexedCount,
        skipped_count: existingOp.scanUnchangedCount ?? 0,
        set_aside: existingOp.scanSetAsideCount
          ? { other: existingOp.scanSetAsideCount }
          : undefined,
        terminal_files: existingOp.terminalFiles ?? undefined,
        walk_files_found: existingOp.walkFilesFound ?? undefined,
      }))
      : null;
    scanOperationId = null;
    useOperationProgressStore.getState().updateOperation(doneId, {
      label: honestDoneLabel ?? doneLabel,
      phase: 'done',
      progress: 1,
    });
    window.setTimeout(() => {
      useOperationProgressStore.getState().endOperation(doneId);
    }, localCore && existingOp?.scanSetAsideCount ? 8000 : 3000);
    startBackgroundCoverEnrichment(albums);
  }

  // Remote access / daemon setup can take 30–45s — never block the grid after scan.
  void setupPostScanRemoteAccess(localCore).catch((e) => {
    console.debug('[scan-coordinator] post-scan remote access setup failed:', e);
  });
}

/** Best-effort tunnel + daemon registration after a successful scan. */
async function setupPostScanRemoteAccess(localCore: boolean): Promise<void> {
  if (typeof window === 'undefined' || !('__TAURI__' in window)) return;
  if (localStorage.getItem('maestro_remote_access') === '0') return;

  try {
    if (!localCore) {
      let allPaths = [...state.selectedPaths];
      try {
        const resp = await libraryService.getSources();
        const sourcePaths = (resp?.sources || []).map((s: { path?: string }) => s.path).filter(Boolean);
        allPaths = [...new Set([...allPaths, ...sourcePaths])];
      } catch { /* best-effort */ }
      await setLibraryPaths(allPaths);
    }
    const connector = await import('../lib/connector');
    await connector.warmLocalNetworkAccess();
    await connector.enableHomeLibraryRemoteAccess();
  } catch (e) {
    console.debug('[scan-coordinator] post-scan remote access setup failed:', e);
  }
}

export async function requestCancelScan(): Promise<void> {
  cancelRequested = true;
  if (scanOperationId) {
    useOperationProgressStore.getState().updateOperation(scanOperationId, { label: 'Cancelling scan...' });
  }
  try { await nativeCancelScan(); } catch { /* best-effort */ }
}

export function resetScan(): void {
  if (currentRun) {
    console.warn('[scan-coordinator] resetScan() called while scan is active — ignored');
    return;
  }
  resetScanProgressWatchdog();
  resetLocalScanUiCompleted();
  connectorWalkScanActive = false;
  state = { ...INITIAL_STATE };
  if (scanOperationId) {
    useOperationProgressStore.getState().endOperation(scanOperationId);
    scanOperationId = null;
  }
  emit();
}

export async function rescanSources(sourceIds: string[]): Promise<void> {
  if (!sourceIds.length) return;
  await refreshConnectorScanWalkProgress();
  if (isLibraryScanInProgress()) {
    console.log(
      `[Scan] re-scan skipped — connector scan already active ts=${new Date().toISOString()}`,
    );
    return;
  }
  const resp = await libraryService.getSources();
  const allSources: Array<{ id?: string; path?: string }> = resp?.sources || [];
  const matching = allSources.filter((s) => sourceIds.includes(s.id || ''));
  const paths = matching.map((s) => s.path).filter(Boolean) as string[];
  if (!paths.length) return;

  const scanSources: ScanSource[] = matching
    .filter((s): s is { id: string; path: string } => !!(s.id && s.path))
    .map((s) => ({ id: s.id, path: s.path }));

  console.log(
    `[Scan] re-scan requested: ${sourceIds.length} source(s), ` +
    `${scanSources.length} with IDs, ${paths.length} paths ts=${new Date().toISOString()}`,
  );

  return startScan({ selectedPaths: paths, scanSources });
}

// Ingest a chunk of tracks to the backend, with retry.
// skip_rebuild=true tells the backend to skip per-folder album materialization.
async function ingestChunk(
  tracks: ScannedTrack[],
  sourceId: string | null,
  machineId: string | undefined,
): Promise<number> {
  let failedBatches = 0;
  for (let i = 0; i < tracks.length; i += INGEST_BATCH_SIZE) {
    const batch = tracks.slice(i, i + INGEST_BATCH_SIZE);
    let attempt = 0;
    let succeeded = false;
    for (;;) {
      try {
        await libraryService.ingestTracks(batch, sourceId, machineId, true);
        succeeded = true;
        break;
      } catch (e: any) {
        attempt += 1;
        if (attempt >= 3) {
          console.warn(
            `[scan-coordinator] ingest batch ${i}..${i + batch.length} failed after 3 attempts:`,
            e?.message || e,
          );
          break;
        }
        await new Promise((r) => setTimeout(r, 1000 * attempt));
      }
    }
    if (!succeeded) failedBatches++;
    streamIngestedCount += batch.length;
    setState({ ingestDone: streamIngestedCount });
  }
  return failedBatches;
}

async function resolveScanSourcesForRun(sources: ScanSource[]): Promise<ScanSource[]> {
  if (isTargetedScanPlan(sources)) {
    return dedupeTargetedScanSources(sources);
  }

  let next = sources;
  if (sources.length >= MULTI_SELECT_SCAN_THRESHOLD) {
    const collapsed = collapseBulkScanPlan({
      selectedPaths: sources.map((s) => s.scan_path || s.path),
      scanSources: sources,
    });
    next = collapsed.scanSources || sources;
  }

  const hasSubfolderScan = next.some((s) => {
    const sub = s.scan_path ? normScanPath(s.scan_path) : '';
    const root = normScanPath(s.path);
    return sub && sub !== root;
  });
  if (hasSubfolderScan || next.length !== 1) return next;

  const sole = next[0];
  const scanTarget = normScanPath(sole.scan_path || sole.path);
  const sourceRoot = normScanPath(sole.path);
  if (scanTarget === sourceRoot) return next;

  try {
    const resp = await libraryService.getSources();
    const registered = (resp?.sources || []) as Array<{ id?: string; path?: string }>;
    const enclosing = findEnclosingSource([scanTarget], registered);
    if (enclosing) {
      console.log(`[Scan] Using registered library root ${enclosing.path} for subfolder scan`);
      return [{ id: enclosing.id, path: enclosing.path, scan_path: scanTarget }];
    }
    const exact = registered.find((s) => normScanPath(s.path || '') === scanTarget);
    if (exact?.id) {
      return [{ id: exact.id, path: scanTarget }];
    }
    const label = scanTarget.split('/').filter(Boolean).pop() || 'Music';
    const created = await libraryService.createSource({ path: scanTarget, label });
    const id = created?.source?.id;
    if (id) {
      console.log(`[Scan] Registered library root ${scanTarget} for scan`);
      return [{ id, path: scanTarget }];
    }
  } catch (e) {
    console.warn('[Scan] scan source resolve failed — using plan as-is', e);
  }
  return next;
}

async function registerLibrarySourcesFromPaths(
  paths: string[],
  machineId?: string,
): Promise<ScanSource[]> {
  if (!paths.length) return [];
  const items = paths.map((p) => ({
    path: p,
    label: p.replace(/\\/g, '/').split('/').filter(Boolean).pop() || 'Music',
    is_temporary: false as const,
    ...(machineId ? { machine_id: machineId } : {}),
  }));
  try {
    console.log('[Scan] local_library_add_sources payload', {
      pathCount: paths.length,
      paths,
    });
    const resp = await libraryService.createSources(items);
    const built: ScanSource[] = [];
    for (let i = 0; i < paths.length; i++) {
      const r = resp?.results?.[i];
      const id = r?.source?.id;
      const path = r?.source?.path || paths[i];
      if (id) built.push({ id, path });
    }
    return built;
  } catch (e) {
    console.warn('[Scan] batch source registration failed', e);
    return [];
  }
}

async function runLocalCoreScan(): Promise<void> {
  try {
    let scanSources = activeRunScanSources?.length
      ? [...activeRunScanSources]
      : state.scanSources;
    if (scanSources?.length) {
      scanSources = await resolveScanSourcesForRun(scanSources);
      scanSources = dedupeTargetedScanSources(scanSources);
      activeRunScanSources = scanSources;
      setState({
        scanSources,
        selectedPaths: scanSources.map((s) => s.scan_path || s.path),
      });
    }
    if (!scanSources?.length) {
      const built = await registerLibrarySourcesFromPaths(state.selectedPaths);
      scanSources = built;
      setState({ scanSources: built });
      console.log(
        `[Scan] local-core: registered ${built.length} source(s) ts=${new Date().toISOString()}`,
      );
    } else {
      console.log(
        `[Scan] local-core: using ${scanSources.length} pre-registered source(s) ts=${new Date().toISOString()}`,
      );
    }

    if (!scanSources?.length) {
      throw new Error('No library sources to scan');
    }

    unlistenLocalProgress = await onLocalScanProgress((p) => {
      bumpScanDisplayPeaks(p as Record<string, unknown>, state);
      setState({ nativeProgress: p as ScanProgress });
      applyLocalProgressToOperation(p);
    });

    console.log(
      `[Scan] local-core scan started for ${scanSources.length} folder(s) ts=${new Date().toISOString()}`,
    );
    for (const s of scanSources) {
      const walk = s.scan_path || s.path;
      console.log(
        `[Scan] walk target: ${walk}${s.scan_path && s.scan_path !== s.path ? ` (library root: ${s.path})` : ''}`,
      );
    }
    const summary = await scanLocalLibrarySources(scanSources);

    if (summary.skipped_sources?.length && summary.skipped_sources.length >= scanSources.length) {
      throw new Error(
        'No folders were scanned — library paths may be offline or not registered. ' +
        'Check that /Volumes/share is mounted, then try again.',
      );
    }
    if (unlistenLocalProgress) {
      unlistenLocalProgress();
      unlistenLocalProgress = null;
    }

    if (cancelRequested) {
      setState({ phase: 'cancelled', active: false });
      invalidateLibraryStore();
      if (scanOperationId) {
        const cancelledId = scanOperationId;
        useOperationProgressStore.getState().updateOperation(cancelledId, {
          label: 'Scan cancelled',
          phase: 'cancelled',
          progress: 1,
        });
        scanOperationId = null;
        window.setTimeout(() => {
          useOperationProgressStore.getState().endOperation(cancelledId);
        }, 3000);
      }
      return;
    }

    const newOrChanged = summary.files_new_or_changed ?? 0;
    const unchanged = summary.files_unchanged ?? 0;
    console.log(
      `[Scan] local-core scan complete: ${newOrChanged} new/changed file(s), ` +
      `${unchanged} unchanged, ${summary.total_albums ?? '?'} albums in library ts=${new Date().toISOString()}`,
    );
    if (newOrChanged === 0 && (state.selectedPaths?.length || scanSources.length)) {
      console.warn(
        '[Scan] No new or changed audio files under the scanned folder(s). ' +
        'If you added a DSD/DSF file earlier, run Scan again — unchanged files with missing tags are re-processed automatically. ' +
        'Otherwise put music inside a watched folder (Browse Folders → Add to Library).',
      );
    }
    await finishScanSuccess(summary, false, { localCore: true });
  } catch (err: any) {
    console.error('[Scan] local-core scan failed:', err?.message || err, err);
    if (isPermissionDeniedMessage(err?.message)) {
      const ids = (scanSources || [])
        .map((s) => s.id)
        .filter((id): id is string => Boolean(id));
      if (ids.length) {
        window.dispatchEvent(new CustomEvent('maestro:source-permission-blocked', {
          detail: { sourceIds: ids },
        }));
      }
    }
    if (unlistenLocalProgress) {
      try { unlistenLocalProgress(); } catch {}
      unlistenLocalProgress = null;
    }
    setState({
      phase: 'error',
      error: err?.message || 'Scan failed',
      active: false,
    });
    if (scanOperationId) {
      const errId = scanOperationId;
      useOperationProgressStore.getState().updateOperation(errId, {
        label: err?.message || 'Scan failed',
        phase: 'error',
        progress: 1,
      });
      scanOperationId = null;
      window.setTimeout(() => {
        useOperationProgressStore.getState().endOperation(errId);
      }, 4000);
    }
  }
}

async function runScan(): Promise<void> {
  await probeLocalLibraryHost().catch(() => {});
  const useLocalCore = isTauriConnector() && (await probeLocalLibraryIpc());
  console.log(
    `[Scan] engine: ${useLocalCore ? 'local-core (SQLite)' : 'legacy cloud ingest'} ts=${new Date().toISOString()}`,
  );
  if (useLocalCore) {
    await runLocalCoreScan();
    return;
  }

  let sourceId: string | null = null;
  const isRescan = !!(state.scanSources && state.scanSources.length > 0);

  try {
    // 0. Push current access token to Connector so scan/reconcile commands authenticate
    try {
      const stored = localStorage.getItem('maestro_auth');
      const token = stored ? JSON.parse(stored).accessToken : null;
      if (token) await setAuthToken(token);
    } catch { /* best-effort */ }

    // 1. Machine ID
    let machineId: string | undefined;
    try { machineId = await getMachineId(); } catch { machineId = undefined; }
    if (machineId) {
      try { localStorage.setItem('maestro_machine_id', machineId); } catch {}
    }
    setState({ machineId });

    // 2. Subscribe to native progress events
    unlistenNative = await onScanProgress((p: ScanProgress) => {
      bumpScanDisplayPeaks(p as Record<string, unknown>, state);
      setState({ nativeProgress: p });
      if (scanOperationId) {
        useOperationProgressStore.getState().updateOperation(scanOperationId, {
          label: p?.phase === 'scan-complete' ? 'Finalizing scan...' : 'Scanning...',
          progress: typeof p?.percent === 'number' ? p.percent / 100 : undefined,
          totalSteps: typeof p?.files_found === 'number' ? p.files_found : undefined,
          currentStep: typeof p?.files_processed === 'number' ? p.files_processed : undefined,
        });
      }
    });

    // 3. Resolve source IDs (pre-built from Finder, or create for cloud ingest path)
    if (state.scanSources && state.scanSources.length > 0) {
      sourceId = state.scanSources[0].id;
      console.log(
        `[Scan] using ${state.scanSources.length} existing source ID(s), ` +
        `primary=${sourceId} ts=${new Date().toISOString()}`,
      );
    } else {
      const built = await registerLibrarySourcesFromPaths(state.selectedPaths, machineId);
      const sourceIds = built.map((s) => s.id).filter(Boolean) as string[];
      sourceId = sourceIds[0] || null;
      console.log(
        `[Scan] fresh scan: created ${sourceIds.length} source(s), ` +
        `primary=${sourceId || '(none)'} ts=${new Date().toISOString()}`,
      );
    }

    // 4. Subscribe to scan-batch events — ingest each batch as it arrives
    setState({ phase: 'scanning', ingestTotal: 0, ingestDone: 0 });
    let totalFailedBatches = 0;

    // Batch ingestion runs as fire-and-forget promises queued sequentially
    let ingestChain = Promise.resolve();

    unlistenBatch = await onScanBatch((tracks: ScannedTrack[]) => {
      if (!tracks || tracks.length === 0) return;
      setState({
        ingestTotal: (state.ingestTotal || 0) + tracks.length,
        phase: 'ingesting',
      });
      if (scanOperationId) {
        const nextTotal = (state.ingestTotal || 0) + tracks.length;
        useOperationProgressStore.getState().updateOperation(scanOperationId, {
          label: 'Ingesting scan results...',
          totalSteps: nextTotal,
        });
      }
      ingestChain = ingestChain.then(async () => {
        const failed = await ingestChunk(tracks, sourceId, machineId);
        totalFailedBatches += failed;
      });
    });

    // 5. Start the scan — pass source IDs when available for incremental scanning
    let summary: ScanSummary;
    const scanSources = state.scanSources?.length ? state.scanSources : undefined;
    console.log(
      `[Scan] scan started for ${state.selectedPaths.length} folder(s): ` +
      state.selectedPaths.map((p) => `"${p}"`).join(', ') +
      ` incremental=${isRescan} ts=${new Date().toISOString()}`,
    );
    try {
      summary = await scanLibrary(state.selectedPaths, scanSources);
    } catch (e: any) {
      // Older Connector versions return Vec<ScannedTrack> — fall back to legacy path
      const legacyResult = e as any;
      if (Array.isArray(legacyResult)) {
        console.info('[scan-coordinator] Legacy Connector detected — falling back to array ingest');
        setState({ phase: 'ingesting', ingestTotal: legacyResult.length, ingestDone: 0 });
        streamIngestedCount = 0;
        totalFailedBatches = await ingestChunk(legacyResult, sourceId, machineId);
        summary = {
          total_tracks: legacyResult.length,
          total_albums: state.nativeProgress?.albums_identified || 0,
          duration_secs: 0,
        };
      } else {
        throw e;
      }
    }

    // Wait for all streaming ingestion to complete
    await ingestChain;

    if (unlistenNative) { unlistenNative(); unlistenNative = null; }
    if (unlistenBatch) { unlistenBatch(); unlistenBatch = null; }

    if (cancelRequested || state.nativeProgress?.phase === 'cancelled') {
      const albums = await fetchRealAlbumCount(summary.total_albums);
      setState({
        phase: 'cancelled',
        trackCount: summary.total_tracks,
        albumCount: albums,
        active: false,
      });
      // Still rebuild albums for whatever was ingested
      try { await libraryService.rebuildAlbums(); } catch {}
      invalidateLibraryStore();
      if (scanOperationId) {
        const cancelledId = scanOperationId;
        useOperationProgressStore.getState().updateOperation(cancelledId, {
          label: 'Scan cancelled',
          phase: 'cancelled',
          progress: 1,
        });
        scanOperationId = null;
        window.setTimeout(() => {
          useOperationProgressStore.getState().endOperation(cancelledId);
        }, 3000);
      }
      return;
    }

    if (totalFailedBatches > 0) {
      console.warn(`[scan-coordinator] ${totalFailedBatches} batch(es) failed during cloud ingest`);
    }

    try {
      await libraryService.rebuildAlbums();
    } catch (e: any) {
      console.warn('[scan-coordinator] album rebuild after ingest failed:', e?.message || e);
    }

    console.log(
      `[Scan] cloud scan complete: ${summary.total_tracks} tracks ts=${new Date().toISOString()}`,
    );
    await finishScanSuccess(summary, totalFailedBatches > 0);
  } catch (err: any) {
    console.error('[Scan] legacy/cloud scan failed:', err?.message || err, err);
    if (unlistenNative) {
      try { unlistenNative(); } catch {}
      unlistenNative = null;
    }
    if (unlistenBatch) {
      try { unlistenBatch(); } catch {}
      unlistenBatch = null;
    }
    setState({
      phase: 'error',
      error: err?.message || 'Scan failed',
      active: false,
    });
    if (scanOperationId) {
      const errId = scanOperationId;
      useOperationProgressStore.getState().updateOperation(errId, {
        label: err?.message || 'Scan failed',
        phase: 'error',
        progress: 1,
      });
      scanOperationId = null;
      window.setTimeout(() => {
        useOperationProgressStore.getState().endOperation(errId);
      }, 4000);
    }
  }
}

function invalidateLibraryStore(): void {
  try {
    useLibraryStore.getState().invalidate();
  } catch { /* store may not be available */ }
  try {
    // Bust version-only cache hits — local diff returns metadata rows, not albums.
    useLibraryCacheStore.getState().setVersion(null);
  } catch { /* cache store optional */ }
}

async function fetchRealAlbumCount(fallback: number): Promise<number> {
  try {
    const stats = await libraryService.getStats();
    const n = (stats as any)?.total_albums ?? (stats as any)?.albums;
    if (stats?.success && typeof n === 'number' && n > 0) return n;
  } catch { /* use fallback */ }
  return fallback;
}
